import { Controller } from "@hotwired/stimulus"

const SEARCH_DEBOUNCE_MS = 300
const MIN_SEARCH_LENGTH = 2

export default class extends Controller {
  static targets = [
    "searchInput",
    "clearSearchBtn",
    "filterPanel",
    "filterToggle",
    "filterBadge",
    "clearFiltersBtn",
    "resultsCount",
    "content",
    "liveRegion",
    "sortIcon",
    "filterDrawer",
    "filterDrawerBackdrop",
    "filterDrawerToggle",
    "loadingOverlay"
  ]

  static values = {
    url: String,
    mobileCollapsible: { type: Boolean, default: true },
    currentSort: { type: String, default: "" },
    i18nResultsNone: { type: String, default: "No results found" },
    i18nResultsOne: { type: String, default: "1 result found" },
    i18nResultsOther: { type: String, default: "{count} results found" },
    i18nError: { type: String, default: "Request failed" }
  }

  connect() {
    this.debouncedSearch = this.debounce(this.performRequest.bind(this), SEARCH_DEBOUNCE_MS)
    this.initFromUrl()
  }

  disconnect() {
    // Cleanup en cas de navigation Turbo alors que le drawer de filtres est ouvert :
    // sans ça, le listener `keydown` et le lock de scroll survivent. On ne relâche
    // le scroll que si c'est nous qui l'avons verrouillé (sinon on écrase une modal).
    if (this.escapeHandler) {
      document.removeEventListener("keydown", this.escapeHandler)
      this.escapeHandler = null
    }
    if (this.scrollLocked) {
      document.body.style.overflow = ""
      this.scrollLocked = false
    }
  }

  initFromUrl() {
    const params = new URLSearchParams(window.location.search)

    if (params.has("sort")) {
      this.currentSortValue = params.get("sort")
      this.updateSortIndicators()
    }
  }

  search() {
    const query = this.getSearchQuery()
    this.toggleClearSearchButton(query.length > 0)

    if (query.length >= MIN_SEARCH_LENGTH || query.length === 0) {
      this.debouncedSearch()
    }
  }

  handleSearchKeydown(event) {
    if (event.key === "Escape") {
      this.clearSearch()
    }
  }

  clearSearch() {
    if (!this.hasSearchInputTarget) return

    this.searchInputTarget.value = ""
    this.toggleClearSearchButton(false)
    this.performRequest()
    this.searchInputTarget.focus()
  }

  toggleFilters() {
    if (!this.hasFilterPanelTarget) return

    const isHidden = this.filterPanelTarget.classList.toggle("hidden")

    if (this.hasFilterToggleTarget) {
      this.filterToggleTarget.setAttribute("aria-expanded", !isHidden)
    }

    if (!isHidden) {
      this.filterPanelTarget.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }
  }

  toggleFilterDrawer() {
    if (!this.hasFilterDrawerTarget) return

    const isHidden = this.filterDrawerTarget.classList.contains("hidden")

    if (isHidden) {
      this.openFilterDrawer()
    } else {
      this.closeFilterDrawer()
    }
  }

  openFilterDrawer() {
    if (!this.hasFilterDrawerTarget) return

    if (this.hasFilterDrawerBackdropTarget) {
      this.filterDrawerBackdropTarget.classList.remove("hidden")
    }

    this.filterDrawerTarget.classList.remove("hidden")

    requestAnimationFrame(() => {
      this.filterDrawerTarget.classList.remove("translate-x-full")
    })

    if (this.hasFilterDrawerToggleTarget) {
      this.filterDrawerToggleTarget.setAttribute("aria-expanded", "true")
    }

    document.body.style.overflow = "hidden"
    this.scrollLocked = true

    setTimeout(() => {
      const firstInput = this.filterDrawerTarget.querySelector("select, input")
      if (firstInput) firstInput.focus()
    }, 100)

    this.escapeHandler = this.handleEscapeKey.bind(this)
    document.addEventListener("keydown", this.escapeHandler)
  }

  closeFilterDrawer() {
    if (!this.hasFilterDrawerTarget) return

    this.filterDrawerTarget.classList.add("translate-x-full")

    if (this.hasFilterDrawerBackdropTarget) {
      this.filterDrawerBackdropTarget.classList.add("hidden")
    }

    setTimeout(() => {
      this.filterDrawerTarget.classList.add("hidden")
    }, 300)

    if (this.hasFilterDrawerToggleTarget) {
      this.filterDrawerToggleTarget.setAttribute("aria-expanded", "false")
    }

    document.body.style.overflow = ""
    this.scrollLocked = false

    if (this.escapeHandler) {
      document.removeEventListener("keydown", this.escapeHandler)
      this.escapeHandler = null
    }

    if (this.hasFilterDrawerToggleTarget) {
      this.filterDrawerToggleTarget.focus()
    }
  }

  handleEscapeKey(event) {
    if (event.key === "Escape") {
      this.closeFilterDrawer()
    }
  }

  applyFilters(event) {
    this.updateSelectActiveState(event)
    this.updateFilterBadge()
    this.updateClearFiltersButton()
    this.performRequest()
  }

  updateSelectActiveState(event) {
    if (event && event.target.tagName === "SELECT") {
      const select = event.target
      if (select.value) {
        select.setAttribute("data-active", "true")
      } else {
        select.removeAttribute("data-active")
      }
    }
  }

  clearAllFilters() {
    this.resetAllFilterInputs()
    this.updateFilterBadge()
    this.updateClearFiltersButton()
    this.performRequest()
  }

  sortByColumn(event) {
    const column = event.currentTarget.dataset.column
    const newSort = this.calculateNextSort(column)

    this.currentSortValue = newSort
    this.updateSortIndicators()
    this.performRequest()
  }

  goToPage(event) {
    event.preventDefault()
    const page = event.currentTarget.dataset.page
    if (page) {
      const params = new URLSearchParams(window.location.search)
      params.set("page", page)
      this.performRequestWithParams(params)
    }
  }

  showLoading() {
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.remove("hidden")
      this.loadingOverlayTarget.classList.add("flex")
    }
  }

  hideLoading() {
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.add("hidden")
      this.loadingOverlayTarget.classList.remove("flex")
    }
  }

  async performRequest() {
    const params = this.buildQueryParams()
    await this.performRequestWithParams(params)
  }

  async performRequestWithParams(params) {
    this.updateBrowserUrl(params)
    this.showLoading()

    try {
      const response = await fetch(`${this.urlValue}?${params}`, {
        headers: {
          "Accept": "text/vnd.turbo-stream.html",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const html = await response.text()
      Turbo.renderStreamMessage(html)

      // Update sort indicators after Turbo Stream renders new content
      setTimeout(() => {
        this.updateSortIndicators()
        this.announceResults()
      }, 100)
    } catch (error) {
      console.error("Request error:", error)
      this.announceLiveRegion(this.i18nErrorValue)
    } finally {
      this.hideLoading()
    }
  }

  buildQueryParams() {
    const params = new URLSearchParams()

    const query = this.getSearchQuery()
    if (query) {
      params.set("q", query)
    }

    this.appendFilterParams(params)

    if (this.currentSortValue) {
      params.set("sort", this.currentSortValue)
    }

    return params
  }

  appendFilterParams(params) {
    const filterElements = this.element.querySelectorAll("[data-filter-param]")

    filterElements.forEach(element => {
      const paramName = element.dataset.filterParam
      const filterType = element.dataset.filterType

      if (!paramName) return

      let value = ""

      switch (filterType) {
        case "checkbox":
          value = element.checked ? "1" : ""
          break
        case "select":
        case "date":
        default:
          value = element.value || ""
          break
      }

      if (value) {
        params.set(paramName, value)
      }
    })
  }

  getSearchQuery() {
    return this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : ""
  }

  toggleClearSearchButton(show) {
    if (!this.hasClearSearchBtnTarget) return

    if (show) {
      this.clearSearchBtnTarget.classList.remove("hidden")
    } else {
      this.clearSearchBtnTarget.classList.add("hidden")
    }
  }

  calculateNextSort(column) {
    const current = this.currentSortValue

    if (current === `${column}:asc`) {
      return `${column}:desc`
    } else if (current === `${column}:desc`) {
      return ""
    } else {
      return `${column}:asc`
    }
  }

  updateSortIndicators() {
    if (!this.hasSortIconTarget) return

    this.sortIconTargets.forEach(icon => {
      const column = icon.dataset.column
      const sortState = this.getSortStateForColumn(column)

      icon.className = this.getSortIconClass(sortState)
    })
  }

  getSortStateForColumn(column) {
    if (this.currentSortValue === `${column}:asc`) return "asc"
    if (this.currentSortValue === `${column}:desc`) return "desc"
    return "none"
  }

  getSortIconClass(state) {
    const baseClass = "fa-solid"
    switch (state) {
      case "asc":
        return `${baseClass} fa-sort-up text-blue-600`
      case "desc":
        return `${baseClass} fa-sort-down text-blue-600`
      default:
        return `${baseClass} fa-sort text-gray-400 group-hover:text-gray-600`
    }
  }

  countActiveFilters() {
    let count = 0
    const filterElements = this.element.querySelectorAll("[data-filter-param]")

    filterElements.forEach(element => {
      const filterType = element.dataset.filterType

      switch (filterType) {
        case "checkbox":
          if (element.checked) count++
          break
        case "select":
          if (element.value) count++
          break
        case "date":
          if (element.value) count++
          break
        default:
          if (element.value) count++
          break
      }
    })

    return count
  }

  updateFilterBadge() {
    if (!this.hasFilterBadgeTarget) return

    const count = this.countActiveFilters()
    this.filterBadgeTarget.textContent = count

    if (count > 0) {
      this.filterBadgeTarget.classList.remove("hidden")
    } else {
      this.filterBadgeTarget.classList.add("hidden")
    }
  }

  updateClearFiltersButton() {
    if (!this.hasClearFiltersBtnTarget) return

    const count = this.countActiveFilters()

    if (count > 0) {
      this.clearFiltersBtnTarget.classList.remove("hidden", "opacity-50", "cursor-not-allowed")
      this.clearFiltersBtnTarget.disabled = false
    } else {
      this.clearFiltersBtnTarget.classList.add("opacity-50", "cursor-not-allowed")
      this.clearFiltersBtnTarget.disabled = true
      if (!this.hasFilterDrawerTarget) {
        this.clearFiltersBtnTarget.classList.add("hidden")
      }
    }
  }

  resetAllFilterInputs() {
    const filterElements = this.element.querySelectorAll("[data-filter-param]")

    filterElements.forEach(element => {
      const filterType = element.dataset.filterType

      switch (filterType) {
        case "checkbox":
          element.checked = false
          break
        case "select":
          element.value = ""
          element.removeAttribute("data-active")
          break
        case "date":
          element.value = ""
          break
        default:
          element.value = ""
          break
      }
    })
  }

  updateBrowserUrl(params) {
    const paramsString = params.toString()
    const newUrl = paramsString ? `${this.urlValue}?${paramsString}` : this.urlValue
    window.history.replaceState({}, "", newUrl)
  }

  announceResults() {
    if (!this.hasResultsCountTarget) return

    const count = parseInt(this.resultsCountTarget.dataset.resultsCount || "0", 10)
    const message = this.buildResultsMessage(count)

    this.announceLiveRegion(message)
  }

  buildResultsMessage(count) {
    if (count === 0) return this.i18nResultsNoneValue
    if (count === 1) return this.i18nResultsOneValue
    return this.i18nResultsOtherValue.replace("{count}", count)
  }

  announceLiveRegion(message) {
    if (!this.hasLiveRegionTarget) return

    this.liveRegionTarget.textContent = message

    setTimeout(() => {
      this.liveRegionTarget.textContent = ""
    }, 1000)
  }

  debounce(func, wait) {
    let timeout
    return (...args) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func(...args), wait)
    }
  }
}
